import {Action, createAction, PayloadAction} from '@reduxjs/toolkit';
import {useDispatch, useSelector} from 'react-redux';
import type {ModalsState} from './modalsReducer';

/**
 * @private don't use outside of modals module
 */
export type ModalHandle<D> = ReturnType<typeof Modals.create<D>>;

/**
 * @private don't use outside of modals module
 */
export type Modal<D = unknown> = ReturnType<typeof Modals.open<D>>['payload'];

export namespace Modals {
    const tag = Symbol();

    type ModalId = string;

    enum ModalMode {
        Opened = 'opened',
        Closed = 'closed',
    }

    interface TaggedModal {
        id: ModalId;
        [tag]: undefined;
    }

    interface OpenedModal<TData> extends TaggedModal {
        data: TData;
        mode: ModalMode.Opened;
    }

    interface ClosedModal extends TaggedModal {
        mode: ModalMode.Closed;
    }

    interface ModalHandle<TData = void> extends ClosedModal {
        /**
         * Checking `handle.id === id` instead of `handle[tag] === id` because symbols aren't serializable and
         * might be missing after deserialization. Avoid type casting, access modals only via handles
         */
        is: (modal: TaggedModal) => modal is OpenedModal<TData>;
        id: ModalId;
    }

    type ModalPayloadAction<T extends string> = PayloadAction<TaggedModal, T>;
    type OpenModalPayloadAction<T extends string, TData> = PayloadAction<OpenedModal<TData>, T>;
    type CloseModalPayloadAction<T extends string> = PayloadAction<ClosedModal, T>;
    type BAC<T extends string> = {
        type: T;
        match: (action: unknown) => action is ModalPayloadAction<T>;
    };

    interface ModalActionCreator<T extends string> extends BAC<T> {
        <TData>(handle: ModalHandle<TData>, ...[data]: VoidToOptional<[TData]>): ModalPayloadAction<T>;
    }

    const createModalAction = <T extends string>(type: T): ModalActionCreator<T> => {
        const creator = createAction(type, payloadType<OpenedModal<unknown>>);
        const wrapper: ModalActionCreator<T> = ({id}, ...[data]) =>
            creator({id, data, mode: ModalMode.Opened, [tag]: undefined});
        wrapper.match = creator.match;
        wrapper.type = creator.type;
        return wrapper;
    };

    const payloadType = <T>(modal: T) => ({payload: modal});

    const createHandle = <TData = void>(id: ModalId): ModalHandle<TData> => {
        return {
            is: (handle): handle is OpenedModal<TData> => handle.id === id,
            id: id,
            mode: ModalMode.Closed,
            [tag]: undefined,
        };
    };

    export interface WithModalsSlice {
        modals: ModalsState;
    }

    const defaultSelector = (state: WithModalsSlice) => state.modals.__current;

    export const select =
        <D>(handle: ModalHandle<D>, selectCurrent = defaultSelector) =>
        (state: WithModalsSlice) => {
            const current = selectCurrent(state);
            const isOpen = current && handle.is(current);
            if (isOpen) {
                return [true, current.data] as const;
            } else {
                return [false, null] as const;
            }
        };

    export const selectIsOpen =
        <D>(handle: ModalHandle<D>, selectCurrent = defaultSelector) =>
        (state: WithModalsSlice) =>
            select(handle, selectCurrent)(state)[0];

    export const selectData =
        <D>(handle: ModalHandle<D>, selectCurrent = defaultSelector) =>
        (state: WithModalsSlice) =>
            select(handle, selectCurrent)(state)[1];

    export const toggle = createModalAction('modals/toggle');
    export const open = createModalAction('modals/open');
    export const clear = createAction('modals/clear');
    export const close = createAction('modals/close', payloadType<ClosedModal>);
    export const create = createHandle;

    export const bindActions = <TData>(handle: ModalHandle<TData>) => {
        const boundOpen = (data: UndefinedToVoid<TData>) => open(handle, data);
        boundOpen.match = (action: Action): action is OpenModalPayloadAction<typeof open.type, TData> => {
            return open.match(action) && action.payload.id === handle.id;
        };
        const boundClose = () => close(handle);
        boundClose.match = (action: Action): action is CloseModalPayloadAction<typeof close.type> => {
            return close.match(action) && action.payload.id === handle.id;
        };
        return {
            open: boundOpen,
            close: boundClose,
        };
    };
}

export const useModal = <TData>(handle: ModalHandle<TData>) => {
    const dispatch = useDispatch();
    const [isOpen, data] = useSelector(Modals.select(handle));
    const api = {
        data,
        open: (...[data]: VoidToOptional<[TData]>) => dispatch(Modals.open(handle, data)),
        close: () => dispatch(Modals.close(handle)),
        clear: () => dispatch(Modals.clear()),
    };
    return [isOpen, api] as const;
};

type VoidToOptional<T extends any[]> = T extends [infer Head, ...infer Tail]
    ? void extends Head
        ? [Head?, ...VoidToOptional<Tail>]
        : [Head, ...VoidToOptional<Tail>]
    : [];

type UndefinedToVoid<T> = T | (T extends undefined ? void : T);
